import { BoxProps } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";
import { ReactNode } from "react";
import Error from "./error";
import Loader from "./loader";
import { extractMessageFromError } from "@/shared/notification";

type Query<T = any> = UseQueryResult<T, Error>

type SingleQueryReturn<T extends Query> = T extends Query<infer R> ? R : never;

type QueryArrayReturn<T extends Query[]> = { [P in keyof T]: SingleQueryReturn<T[P]>; }

type QueryResult<T extends Query | Query[]> = T extends Query[] ? QueryArrayReturn<T> : T extends Query ? SingleQueryReturn<T> : never;

export interface DataFetcherProps<T extends Query | Query[]> extends Omit<BoxProps, "children"> {
    query: T | Readonly<T>,
    loadMode?: "fetching" | "loading",
    children?: (data: QueryResult<T>) => ReactNode
}

export default function DataFetcher<T extends Query | Query[]>({
    query,
    children,
    loadMode = "fetching",
    ...props
}: DataFetcherProps<T>) {
    const [value, loading, error] = useConditionalFetch(query, loadMode);

    if (loading || value === undefined) return <Loader flex={1} minHeight={"300px"} {...props} />
    if (error) return <Error title={extractMessageFromError(error)} message="" minHeight={"300px"} {...props} />
    return children && children(value)
}

function useConditionalFetch(query: Readonly<Query | Query[]>, loadMode: "fetching" | "loading"): [any, boolean, Error | undefined] {
    if (query instanceof Array) {
        const value = query.map(q => q.data!);
        const loading = query.some(q => loadMode === "fetching" ? q.isFetching : q.isLoading);
        const error = query.find(q => q.error)?.error || undefined;

        return [value, loading, error]
    } else {
        const value = query.data!;
        const loading = loadMode === "fetching" ? query.isFetching : query.isLoading;
        const error = query.error || undefined;
        return [value, loading, error];
    }
}